// Mock data layer for the Fatigue Management module.
//
// ALL fixture data is behind this service so it can be replaced with real
// TanStack Query hooks (against the algorithm-engine endpoints) without
// touching any view. Each getter returns a Promise to mirror an async API and
// to let views exercise their loading/skeleton states.

import type {
  Cohort,
  CohortFatigueSummary,
  FatigueAction,
  FatigueDerivedMetric,
  FatigueOverview,
  FatigueTrendPoint,
  RotaOverlay,
  WearComplianceSummary,
  WearableDailyMetric,
} from "./types";

const SIMULATED_LATENCY_MS = 350;

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), SIMULATED_LATENCY_MS));

// ── Cohorts (realistic operational archetypes) ───────────────────────────────

export const COHORTS: Cohort[] = [
  {
    id: "construction-night",
    name: "Construction — Night crew",
    sector: "Construction",
    site: "Site B",
    shiftType: "night",
    headcount: 38,
    riskTier: "red",
  },
  {
    id: "factory-line-1",
    name: "Factory — Line 1",
    sector: "Manufacturing",
    site: "Plant 2",
    shiftType: "rotating",
    headcount: 64,
    riskTier: "amber",
  },
  {
    id: "civil-eng",
    name: "Civil Engineering — Day crew",
    sector: "Civil Engineering",
    site: "Site A",
    shiftType: "day",
    headcount: 41,
    riskTier: "amber",
  },
  {
    id: "logistics-mixed",
    name: "Logistics — Mixed",
    sector: "Logistics",
    site: "Depot 4",
    shiftType: "mixed",
    headcount: 52,
    riskTier: "amber",
  },
  {
    id: "call-centre",
    name: "Call Centre — Support",
    sector: "Call Centre",
    site: "HQ",
    shiftType: "day",
    headcount: 88,
    riskTier: "green",
  },
  {
    id: "executives",
    name: "Executive team",
    sector: "Office / Executive",
    site: "HQ",
    shiftType: "office",
    headcount: 12,
    riskTier: "green",
  },
];

// ── Org-level headline indicators ────────────────────────────────────────────

const ORG_METRICS: FatigueDerivedMetric[] = [
  {
    key: "fed",
    code: "FED",
    name: "Fatigue Exposure Days",
    question: "How often are people starting work already fatigued?",
    value: 84,
    unit: "per 1,000 workdays",
    fill: 64,
    trend: "up",
    trendLabel: "+18%",
    baselineLabel: "+27 vs baseline",
    goodDirection: "down",
    riskTier: "red",
  },
  {
    key: "lrm",
    code: "LRM",
    name: "Load–Readiness Misalignment",
    question: "Was the work demand appropriate for their physical state?",
    value: 23,
    unit: "% of shifts",
    fill: 46,
    trend: "up",
    trendLabel: "+8%",
    baselineLabel: "+5 vs baseline",
    goodDirection: "down",
    riskTier: "amber",
  },
  {
    key: "cdi",
    code: "CDI",
    name: "Circadian Disruption Index",
    question: "Is the schedule itself disrupting sleep and recovery?",
    value: 41,
    unit: "score (0–100)",
    fill: 41,
    trend: "stable",
    trendLabel: "stable",
    baselineLabel: "−1 vs baseline",
    goodDirection: "down",
    riskTier: "amber",
  },
  {
    key: "pdc",
    code: "PDC",
    name: "Physiological Deviation Cluster",
    question: "Are multiple vitals drifting outside personal baseline?",
    value: 12,
    unit: "% of workforce",
    fill: 24,
    trend: "down",
    trendLabel: "−3%",
    baselineLabel: "−1 vs baseline",
    goodDirection: "down",
    riskTier: "amber",
  },
];

// ── Per-cohort summaries ─────────────────────────────────────────────────────

const COHORT_SUMMARIES: CohortFatigueSummary[] = [
  {
    cohort: COHORTS[0],
    fedPer1000: 168,
    fedPctWorkdays: 17,
    lrmPct: 61,
    cdiScore: 58,
    pdcPct: 18,
    trend: "up",
    riskTier: "red",
    sparkline: [39, 44, 52, 58, 67, 74, 84],
  },
  {
    cohort: COHORTS[1],
    fedPer1000: 96,
    fedPctWorkdays: 10,
    lrmPct: 43,
    cdiScore: 49,
    pdcPct: 13,
    trend: "up",
    riskTier: "amber",
    sparkline: [40, 42, 45, 48, 50, 52, 55],
  },
  {
    cohort: COHORTS[2],
    fedPer1000: 74,
    fedPctWorkdays: 7,
    lrmPct: 36,
    cdiScore: 44,
    pdcPct: 9,
    trend: "stable",
    riskTier: "amber",
    sparkline: [38, 39, 41, 40, 42, 41, 41],
  },
  {
    cohort: COHORTS[3],
    fedPer1000: 88,
    fedPctWorkdays: 9,
    lrmPct: 47,
    cdiScore: 46,
    pdcPct: 12,
    trend: "up",
    riskTier: "amber",
    sparkline: [42, 45, 49, 52, 55, 56, 58],
  },
  {
    cohort: COHORTS[4],
    fedPer1000: 34,
    fedPctWorkdays: 3,
    lrmPct: 18,
    cdiScore: 28,
    pdcPct: 5,
    trend: "down",
    riskTier: "green",
    sparkline: [29, 28, 27, 25, 24, 22, 21],
  },
  {
    cohort: COHORTS[5],
    fedPer1000: 22,
    fedPctWorkdays: 2,
    lrmPct: 12,
    cdiScore: 19,
    pdcPct: 3,
    trend: "stable",
    riskTier: "green",
    sparkline: [18, 19, 18, 17, 18, 16, 17],
  },
];

// ── Wear compliance ──────────────────────────────────────────────────────────

const COHORT_COMPLIANCE: WearComplianceSummary[] = [
  {
    cohortId: "construction-night",
    cohortName: "Construction — Night crew",
    activeUsers: 31,
    enrolledUsers: 38,
    validDataDays: 186,
    missingDataDays: 80,
    coveragePct: 70,
    confidenceScore: 68,
  },
  {
    cohortId: "factory-line-1",
    cohortName: "Factory — Line 1",
    activeUsers: 58,
    enrolledUsers: 64,
    validDataDays: 372,
    missingDataDays: 76,
    coveragePct: 83,
    confidenceScore: 81,
  },
  {
    cohortId: "civil-eng",
    cohortName: "Civil Engineering — Day crew",
    activeUsers: 37,
    enrolledUsers: 41,
    validDataDays: 244,
    missingDataDays: 43,
    coveragePct: 85,
    confidenceScore: 84,
  },
  {
    cohortId: "logistics-mixed",
    cohortName: "Logistics — Mixed",
    activeUsers: 40,
    enrolledUsers: 52,
    validDataDays: 248,
    missingDataDays: 116,
    coveragePct: 64,
    confidenceScore: 59,
  },
  {
    cohortId: "call-centre",
    cohortName: "Call Centre — Support",
    activeUsers: 79,
    enrolledUsers: 88,
    validDataDays: 540,
    missingDataDays: 76,
    coveragePct: 89,
    confidenceScore: 88,
  },
  {
    cohortId: "executives",
    cohortName: "Executive team",
    activeUsers: 9,
    enrolledUsers: 12,
    validDataDays: 56,
    missingDataDays: 28,
    coveragePct: 64,
    confidenceScore: 55,
  },
];

const ORG_COVERAGE: WearComplianceSummary = {
  cohortId: "__org__",
  cohortName: "All cohorts",
  activeUsers: 254,
  enrolledUsers: 295,
  validDataDays: 1646,
  missingDataDays: 419,
  coveragePct: 80,
  confidenceScore: 77,
};

// ── Trend ────────────────────────────────────────────────────────────────────

const ORG_TREND: FatigueTrendPoint[] = [
  { period: "Wk-12", fed: 39, lrm: 15, cdi: 44, pdc: 9 },
  { period: "Wk-10", fed: 44, lrm: 18, cdi: 47, pdc: 10 },
  { period: "Wk-8", fed: 52, lrm: 19, cdi: 49, pdc: 11 },
  { period: "Wk-6", fed: 58, lrm: 21, cdi: 53, pdc: 13 },
  { period: "Wk-4", fed: 67, lrm: 22, cdi: 55, pdc: 13 },
  { period: "Wk-2", fed: 74, lrm: 23, cdi: 57, pdc: 12 },
  { period: "Now", fed: 84, lrm: 23, cdi: 58, pdc: 12 },
];

// ── Rota overlays ────────────────────────────────────────────────────────────

const ROTA: RotaOverlay[] = [
  { cohortId: "construction-night", shiftType: "night", startTime: "22:00", isNightShift: true, overtimeFlag: true, restPeriodHours: 9, consecutiveShifts: 6 },
  { cohortId: "factory-line-1", shiftType: "rotating", startTime: "06:00", isNightShift: false, overtimeFlag: true, restPeriodHours: 11, consecutiveShifts: 4 },
  { cohortId: "civil-eng", shiftType: "day", startTime: "07:30", isNightShift: false, overtimeFlag: false, restPeriodHours: 14, consecutiveShifts: 5 },
  { cohortId: "logistics-mixed", shiftType: "mixed", startTime: "14:00", isNightShift: true, overtimeFlag: true, restPeriodHours: 10, consecutiveShifts: 5 },
  { cohortId: "call-centre", shiftType: "day", startTime: "09:00", isNightShift: false, overtimeFlag: false, restPeriodHours: 15, consecutiveShifts: 4 },
  { cohortId: "executives", shiftType: "office", startTime: "08:30", isNightShift: false, overtimeFlag: false, restPeriodHours: 15, consecutiveShifts: 5 },
];

// ── Actions (impact/ease matrix) ─────────────────────────────────────────────

const ACTIONS: FatigueAction[] = [
  {
    id: "act-1",
    title: "Break the 6-night block on Construction night crew",
    description:
      "Sustained 6-night sequencing is the dominant CDI driver. Cap consecutive nights at 4 and insert a recovery day.",
    impact: 5,
    ease: 3,
    cohortId: "construction-night",
    owner: "Site B Ops Lead",
    dueDate: "2026-07-07",
    status: "in_progress",
    notes: "Rota review booked with planning.",
  },
  {
    id: "act-2",
    title: "Reduce overtime exposure on Logistics — Mixed",
    description:
      "Overtime flag set with rest periods under 11h. Remove discretionary overtime for the next two weeks.",
    impact: 4,
    ease: 4,
    cohortId: "logistics-mixed",
    owner: "Logistics Manager",
    dueDate: "2026-07-04",
    status: "todo",
  },
  {
    id: "act-3",
    title: "Add a recovery/rest opportunity to Factory Line 1",
    description:
      "Insert an additional rest break mid-shift on rotating days where LRM is highest.",
    impact: 3,
    ease: 5,
    cohortId: "factory-line-1",
    owner: "Plant 2 Supervisor",
    dueDate: "2026-07-02",
    status: "todo",
  },
  {
    id: "act-4",
    title: "Monitor Civil Engineering for sustained CDI drift",
    description:
      "CDI is stable but elevated. Keep on watch; no schedule change yet. Re-assess in 2 weeks.",
    impact: 2,
    ease: 5,
    cohortId: "civil-eng",
    owner: "Wellbeing Partner",
    dueDate: "2026-07-11",
    status: "todo",
  },
  {
    id: "act-5",
    title: "Do not assign additional load to Construction night crew",
    description:
      "Red tier with sustained high FED + LRM. Hold load flat and prioritise rest and controls until risk subsides.",
    impact: 5,
    ease: 4,
    cohortId: "construction-night",
    owner: "Operations Director",
    dueDate: "2026-07-01",
    status: "in_progress",
  },
  {
    id: "act-6",
    title: "Push Learning Locker sleep content to amber cohorts",
    description:
      "Light-touch nudge: targeted sleep & recovery content for cohorts trending up but not yet red.",
    impact: 2,
    ease: 5,
    owner: "Wellbeing Partner",
    dueDate: "2026-07-05",
    status: "done",
    notes: "Sent to Factory and Logistics cohorts.",
  },
];

// 14-day cohort daily series for drill-down charts (deterministic, no Date/random).
const buildCohortDaily = (
  cohortId: string,
  readinessBase: number,
  strainBase: number,
  sleepBase: number,
): WearableDailyMetric[] =>
  Array.from({ length: 14 }, (_, i) => ({
    date: `D-${14 - i}`,
    cohortId,
    readiness: Math.round(Math.max(20, Math.min(95, readinessBase + Math.sin(i / 2) * 6 - i * 0.4))),
    strain: Math.round((strainBase + Math.cos(i / 3) * 2 + (i % 4 === 0 ? 2 : 0)) * 10) / 10,
    sleepHours: Math.round((sleepBase - (i % 3 === 0 ? 0.7 : 0.1)) * 10) / 10,
    sleepNeed: 7.8,
    restingHr: Math.round(54 + (100 - readinessBase) * 0.2 + Math.sin(i / 2) * 2),
    hrv: Math.round(70 - (100 - readinessBase) * 0.4 + Math.cos(i / 2) * 4),
    respiratoryRate: Math.round((14.5 + (strainBase - 12) * 0.15) * 10) / 10,
  }));

const COHORT_DAILY: Record<string, WearableDailyMetric[]> = {
  "construction-night": buildCohortDaily("construction-night", 48, 16, 5.4),
  "factory-line-1": buildCohortDaily("factory-line-1", 62, 13, 6.6),
  "civil-eng": buildCohortDaily("civil-eng", 68, 12, 7.1),
  "logistics-mixed": buildCohortDaily("logistics-mixed", 60, 14, 6.4),
  "call-centre": buildCohortDaily("call-centre", 78, 9, 7.4),
  executives: buildCohortDaily("executives", 80, 8, 7.2),
};

// ── Public service API (swap these for query hooks later) ───────────────────

export const fatigueService = {
  getOverview: (): Promise<FatigueOverview> =>
    delay({
      metrics: ORG_METRICS,
      topRiskCohorts: [...COHORT_SUMMARIES]
        .sort((a, b) => b.fedPer1000 - a.fedPer1000)
        .slice(0, 3),
      coverage: ORG_COVERAGE,
      trend: ORG_TREND,
    }),

  getCohortSummaries: (): Promise<CohortFatigueSummary[]> =>
    delay([...COHORT_SUMMARIES]),

  getCohort: (cohortId: string): Promise<CohortFatigueSummary | undefined> =>
    delay(COHORT_SUMMARIES.find((c) => c.cohort.id === cohortId)),

  getCohortDaily: (cohortId: string): Promise<WearableDailyMetric[]> =>
    delay(COHORT_DAILY[cohortId] ?? []),

  getRota: (): Promise<RotaOverlay[]> => delay([...ROTA]),

  getRotaForCohort: (cohortId: string): Promise<RotaOverlay | undefined> =>
    delay(ROTA.find((r) => r.cohortId === cohortId)),

  getActions: (): Promise<FatigueAction[]> => delay([...ACTIONS]),

  getCompliance: (): Promise<WearComplianceSummary[]> =>
    delay([...COHORT_COMPLIANCE]),

  getOrgCoverage: (): Promise<WearComplianceSummary> => delay(ORG_COVERAGE),
};
